import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as walletService from './walletService';

function formatAmount(n) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);
}

function formatDate(unixSeconds) {
  try {
    return new Date(parseInt(unixSeconds, 10) * 1000).toLocaleString();
  } catch {
    return String(unixSeconds);
  }
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Generate and download a PDF of a customer's full transaction history.
 * Each row shows the transaction details plus the balance (balance_after) at that time.
 * @param {string|number} customerId
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export async function exportCustomerTransactionsPDF(customerId, t) {
  const result = await walletService.getTransactionsExport(customerId);

  if (!result.success || !result.data) {
    return { success: false, message: result.message || 'Failed to load export data' };
  }

  const { customer, balances = {}, transactions = [] } = result.data;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;
  let y = 50;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text(t('transaction_statement'), marginX, y);

  // Generated date (right aligned)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`${t('generated')}: ${new Date().toLocaleString()}`, pageWidth - marginX, y, { align: 'right' });
  doc.setTextColor(0);

  // Customer info block
  y += 28;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text(customer.name || t('unknown_customer'), marginX, y);

  y += 18;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(80);

  if (customer.phone) {
    doc.text(`${t('phone')}: ${customer.phone}`, marginX, y);
    y += 14;
  }
  if (customer.info) {
    const infoLines = doc.splitTextToSize(`${t('additional_info')}: ${customer.info}`, pageWidth - marginX * 2);
    doc.text(infoLines, marginX, y);
    y += 14 * infoLines.length;
  }
  y += 18;

  // Current balances summary
  const balanceParts = Object.keys(balances).length
    ? Object.entries(balances).map(([cur, amt]) => `${cur}: ${formatAmount(amt)}`)
    : [t('no_wallets')];
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(t('current_balances'), marginX, y);
  y += 14;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(balanceParts.join('    '), marginX, y);
  doc.setTextColor(0);
  y += 10;

  // Transactions table
  // Track running balances for all three currencies, carrying forward unchanged ones.
  // The transaction's own currency uses the authoritative balance_after from the DB.
  const running = { USD: 0, EUR: 0, SRD: 0 };
  const head = [[t('date'), t('type'), t('description'), t('amount'), 'USD', 'EUR', 'SRD']];
  const body = transactions.map((tx) => {
    const amount = parseFloat(tx.amount);
    const sign = amount >= 0 ? '+' : '-';
    const cur = tx.currency;

    // Update the affected currency to its authoritative post-transaction balance
    if (cur in running) {
      running[cur] = parseFloat(tx.balance_after);
    }

    return [
      formatDate(tx.created_at),
      capitalize(tx.type),
      tx.description || '-',
      `${sign}${formatAmount(Math.abs(amount))} ${cur}`,
      formatAmount(running.USD),
      formatAmount(running.EUR),
      formatAmount(running.SRD),
    ];
  });

  autoTable(doc, {
    startY: y + 10,
    head,
    body,
    margin: { left: marginX, right: marginX },
    styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
    headStyles: { fillColor: [73, 66, 91], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 244, 248] },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 70 },
      2: { cellWidth: 'auto' },
      3: { cellWidth: 105, halign: 'right' },
      4: { cellWidth: 75, halign: 'right' },
      5: { cellWidth: 75, halign: 'right' },
      6: { cellWidth: 75, halign: 'right' },
    },
    didParseCell: (data) => {
      // Color amount column based on sign
      if (data.section === 'body' && data.column.index === 3) {
        const raw = data.cell.raw || '';
        if (typeof raw === 'string' && raw.startsWith('-')) {
          data.cell.styles.textColor = [192, 57, 43];
        } else {
          data.cell.styles.textColor = [39, 119, 78];
        }
      }
    },
    didDrawPage: (data) => {
      // Footer with page number
      const pageCount = doc.internal.getNumberOfPages();
      const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `${t('page')} ${pageCurrent} ${t('of')} ${pageCount}`,
        pageWidth - marginX,
        doc.internal.pageSize.getHeight() - 20,
        { align: 'right' }
      );
      doc.text(
        t('brand_title'),
        marginX,
        doc.internal.pageSize.getHeight() - 20
      );
      doc.setTextColor(0);
    },
  });

  if (transactions.length === 0) {
    const afterY = (doc.lastAutoTable && doc.lastAutoTable.finalY) || y + 30;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(t('no_transactions_found_for_customer'), marginX, afterY + 24);
    doc.setTextColor(0);
  }

  const safeName = (customer.name || 'customer').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
  const dateStamp = new Date().toISOString().slice(0, 10);
  doc.save(`statement_${safeName}_${dateStamp}.pdf`);

  return { success: true };
}
